import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCourses } from '../hooks/useCourses';
import { courseService } from '../services/courseService';
import { apiCall } from '../services/api';
import { downloadCertificate } from '../utils/certificateDownload';
import { giroTechLogo, giroTechPlatformName } from '../utils/constants';

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { courses, loading: coursesLoading, fetchCourses, fetchCoursesByGroup } = useCourses(user?.id);
  
  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupCourses, setGroupCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch user groups
  const fetchUserGroups = async () => {
    try {
      const data = await apiCall(`/user-groups?user_id=${user.id}`);
      setUserGroups(data.groups || []);
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  };

  // Load courses progress
  const loadCoursesProgress = async (coursesToLoad = courses) => {
    try {
      const progressPromises = coursesToLoad.map(async (course) => {
        try {
          const progress = await apiCall(`/courses/${course.id}/progress?user_id=${user.id}`);
          return { courseId: course.id, progress };
        } catch (error) {
          console.error(`Error loading progress for course ${course.id}:`, error);
          return { courseId: course.id, progress: null };
        }
      });

      const progressResults = await Promise.all(progressPromises);
      const progressMap = {};
      progressResults.forEach(({ courseId, progress }) => {
        if (progress) {
          progressMap[courseId] = progress;
        }
      });
      setCourseProgress(progressMap);
    } catch (error) {
      console.error('Error loading courses progress:', error);
    }
  };

  // Handle group selection
  const handleGroupSelection = async (group) => {
    setSelectedGroup(group);
    // Update URL to reflect group selection
    navigate(`/dashboard/group/${group.id}`, { replace: true });
    try {
      const data = await courseService.fetchCoursesByGroup(group.id, user.id);
      setGroupCourses(data.courses || []);
      await loadCoursesProgress(data.courses || []);
    } catch (error) {
      console.error('Error fetching group courses:', error);
    }
  };

  // Handle back to all courses
  const handleBackToAllCourses = () => {
    setSelectedGroup(null);
    setGroupCourses([]);
    // Update URL to remove group parameter
    navigate('/dashboard', { replace: true });
    fetchCourses();
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle URL group parameter
  useEffect(() => {
    if (groupId && userGroups.length > 0) {
      const group = userGroups.find(g => g.id === groupId);
      if (group && !selectedGroup) {
        handleGroupSelection(group);
      }
    }
  }, [groupId, userGroups]);

  useEffect(() => {
    if (user) {
      fetchUserGroups();
      loadCoursesProgress();
    }
  }, [user, courses]);

  if (coursesLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img src={giroTechLogo} alt="Giro.Tech" className="h-12 w-auto mr-4" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{giroTechPlatformName}</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard/achievements')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <span>Minhas Conquistas</span>
              </button>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">Olá, {user?.name}</p>
                <p className="text-xs text-gray-500">{user?.company}</p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <button
                onClick={handleLogout}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors duration-200"
              >
                Sair
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {successMessage && (
          <div className="mb-8 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl">
            {successMessage}
          </div>
        )}

        {/* User Groups Section */}
        {userGroups.length > 0 && !selectedGroup && (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Meus Grupos</h2>
              <p className="text-gray-600">Grupos aos quais você pertence e seus cursos exclusivos</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {userGroups.map((group) => (
                <div key={group.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                  <div className="aspect-video relative overflow-hidden">
                    {group.background_image ? (
                      <>
                        <img 
                          src={group.background_image} 
                          alt={group.name}
                          className="w-full h-full object-cover absolute inset-0 z-10"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center absolute inset-0 z-0">
                          <span className="text-white text-xl font-bold">
                            {group.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          {group.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    
                    <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded-md text-xs z-20">
                      Grupo
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{group.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {group.description || ''}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {group.course_count || 0} curso{(group.course_count || 0) !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() => handleGroupSelection(group)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                      >
                        Ver Conteúdos
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedGroup ? `Cursos do Grupo: ${selectedGroup.name}` : 'Seus Cursos'}
              </h2>
              <p className="text-gray-600">
                {selectedGroup 
                  ? `Cursos exclusivos do grupo ${selectedGroup.name}`
                  : 'Escolha um curso para começar sua jornada de aprendizado'
                }
              </p>
            </div>
            {selectedGroup && (
              <button
                onClick={handleBackToAllCourses}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Voltar a Todos os Cursos</span>
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(() => {
            const currentCourses = selectedGroup ? groupCourses : courses;
            return Array.isArray(currentCourses) && currentCourses.map((course) => {
              const progress = courseProgress[course.id];
              const completedVideos = progress?.completed_videos || [];
              const totalVideos = course.videos.length;
              const progressPercentage = totalVideos > 0 ? (completedVideos.length / totalVideos) * 100 : 0;
              const certificateId = progress?.certificate_id;

              return (
                <div key={course.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300 group">
                  {course.background_image && (
                    <div className="h-48 bg-cover bg-center relative overflow-hidden" style={{backgroundImage: `url(${course.background_image})`}}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-lg font-semibold text-white mb-1">{course.title}</h3>
                        <p className="text-white/80 text-sm">{course.videos.length} vídeos</p>
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    {!course.background_image && (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{course.title}</h3>
                        <p className="text-gray-600 text-sm mb-4 whitespace-pre-line">{course.description}</p>
                      </>
                    )}

                    {course.background_image && (
                      <p className="text-gray-600 text-sm mb-4 whitespace-pre-line">{course.description}</p>
                    )}

                    {progress && progressPercentage > 0 && (
                      <div className="mb-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">Progresso</span>
                          <span className="text-sm font-medium text-gray-900">{Math.round(progressPercentage)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progressPercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                      <div className="flex items-center justify-between">
                      {!course.background_image && (
                        <span className="text-sm text-gray-500">{course.videos.length} vídeos</span>
                      )}
                      <div className="flex items-center space-x-2 ml-auto">
                        {certificateId && (
                          <button
                            onClick={() => downloadCertificate(progress.certificate_id)}
                            className="bg-green-100 hover:bg-green-200 text-green-800 px-3 py-1 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                            </svg>
                            <span>Certificado</span>
                          </button>
                        )}
                        <button
                          onClick={() => {
                            const courseUrl = selectedGroup 
                              ? `/course/${course.id}?fromGroup=${selectedGroup.id}`
                              : `/course/${course.id}`;
                            navigate(courseUrl);
                          }}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                        >
                          {progressPercentage > 0 ? 'Continuar' : 'Iniciar Curso'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {(() => {
          const currentCourses = selectedGroup ? groupCourses : courses;
          const hasNoCourses = !Array.isArray(currentCourses) || currentCourses.length === 0;
          
          return hasNoCourses ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedGroup ? `Nenhum curso disponível no grupo ${selectedGroup.name}` : 'Nenhum curso disponível'}
              </h3>
              <p className="text-gray-500">
                {selectedGroup ? 'Este grupo ainda não possui cursos cadastrados.' : 'Novos cursos serão adicionados em breve.'}
              </p>
            </div>
          ) : null;
        })()}
      </div>
    </div>
  );
};

export default DashboardPage; 